import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { calculateDistance, isWithinDistance, sanitizeDistancePreference } from './distanceUtils';
import { createNewProductInventory } from './inventorySchema';

const productsCollection = collection(db, 'products');
const shopsCollection = collection(db, 'shops');

// Debug database connection
  // Database initialized
console.log('🔍 [productApi] Shops collection:', shopsCollection);

// Test database connection by trying to read a document
export async function testDatabaseConnection() {
  try {
    console.log('🔍 [testDatabaseConnection] Testing database connection...');
    const testQuery = query(productsCollection, where('sellerId', '==', 'test'));
    const testSnapshot = await getDocs(testQuery);
    console.log('✅ [testDatabaseConnection] Database connection successful, test query returned:', testSnapshot.size, 'documents');
    return true;
  } catch (error) {
    console.error('❌ [testDatabaseConnection] Database connection failed:', error);
    return false;
  }
}

// Helper function to filter products by status and inventory according to PRD requirements
// PRD: Products must satisfy both conditions:
// 1. status = active (published = true)
// 2. inventory > 0 (or unlimited = true, or inventory = null)
export function filterAvailableProducts(products) {
  // Handle edge cases
  if (!Array.isArray(products)) {
    console.warn('[ProductFilter] filterAvailableProducts: products parameter is not an array');
    return [];
  }
  
  const filtered = products.filter(product => {
    // Skip invalid products
    if (!product || typeof product !== 'object') {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ProductFilter] Skipping invalid product:', product);
      }
      return false;
    }
    
    // Requirement 0: Product must not be archived
    if (product.archived === true) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductFilter] Excluding archived product: ${product.name || 'Unknown'} (ID: ${product.id || 'Unknown'})`);
      }
      return false;
    }
    
    // Requirement 1: Product must be active (published = true OR status = true)
    // Note: Products use 'status' field, but we check both for compatibility
    const isActive = product.published === true || product.status === true;
    if (!isActive) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductFilter] Excluding inactive product: ${product.name || 'Unknown'} (ID: ${product.id || 'Unknown'}) - published: ${product.published}, status: ${product.status}`);
      }
      return false;
    }
    
    // Requirement 2: Product must have available stock (not just planned inventory)
    let hasAvailableStock = false;
    
    // Check if product has unlimited stock flag
    if (product.unlimited === true || product.stock === null) {
      hasAvailableStock = true;
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductFilter] Product has unlimited stock: ${product.name || 'Unknown'} (ID: ${product.id || 'Unknown'})`);
      }
    } else {
      // Check total available stock
      let totalAvailableStock = 0;
      
      // If product has new inventory schema, check scheduled availability
      if (product.inventory && product.inventory.scheduled && Array.isArray(product.inventory.scheduled)) {
        try {
          totalAvailableStock = product.inventory.scheduled.reduce((sum, row) => {
            if (row.enabled && row.available_total > 0) {
              return sum + row.available_total;
            }
            return sum;
          }, 0);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[ProductFilter] New inventory schema total for ${product.name || 'Unknown'}: ${totalAvailableStock}`);
          }
        } catch (error) {
          console.warn('[ProductFilter] Error calculating new inventory schema:', error);
          totalAvailableStock = 0;
        }
      } else if (product.dailyInventory && typeof product.dailyInventory === 'object') {
        // Legacy daily inventory tracking (date-based)
        try {
          totalAvailableStock = Object.values(product.dailyInventory).reduce((sum, stock) => {
            const numStock = Number(stock);
            return sum + (isNaN(numStock) ? 0 : numStock);
          }, 0);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[ProductFilter] Legacy daily inventory total for ${product.name || 'Unknown'}: ${totalAvailableStock}`);
          }
        } catch (error) {
          console.warn('[ProductFilter] Error calculating legacy daily inventory:', error);
          totalAvailableStock = 0;
        }
      } else if (product.dailyStock && typeof product.dailyStock === 'object') {
        // Legacy daily stock tracking (day-based)
        try {
          totalAvailableStock = Object.values(product.dailyStock).reduce((sum, stock) => {
            const numStock = Number(stock);
            return sum + (isNaN(numStock) ? 0 : numStock);
          }, 0);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[ProductFilter] Daily stock total for ${product.name || 'Unknown'}: ${totalAvailableStock}`);
          }
        } catch (error) {
          console.warn('[ProductFilter] Error calculating daily stock:', error);
          totalAvailableStock = 0;
        }
      } else if (product.stock !== undefined) {
        // Use regular stock field (available quantity)
        const numStock = Number(product.stock);
        totalAvailableStock = isNaN(numStock) ? 0 : numStock;
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ProductFilter] Regular stock for ${product.name || 'Unknown'}: ${totalAvailableStock}`);
        }
      }
      
      // Check if any variations have available stock
      if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
        try {
          const variantStock = product.variants.reduce((sum, variant) => {
            if (variant && typeof variant === 'object' && variant.stock !== undefined) {
              const numStock = Number(variant.stock);
              return sum + (isNaN(numStock) ? 0 : numStock);
            }
            return sum;
          }, 0);
          totalAvailableStock = Math.max(totalAvailableStock, variantStock);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[ProductFilter] Variant stock total for ${product.name || 'Unknown'}: ${variantStock}`);
          }
        } catch (error) {
          console.warn('[ProductFilter] Error calculating variant stock:', error);
        }
      }
      
      hasAvailableStock = totalAvailableStock > 0;
    }
    
    if (!hasAvailableStock) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductFilter] Excluding out-of-stock product: ${product.name || 'Unknown'} (ID: ${product.id || 'Unknown'})`);
        console.log(`[ProductFilter] Product details:`, {
          stock: product.stock,
          dailyStock: product.dailyStock,
          inventory: product.inventory,
          dailyInventory: product.dailyInventory,
          variants: product.variants,
          unlimited: product.unlimited
        });
      }
      return false;
    }
    
    return true;
  });
  
  if (process.env.NODE_ENV === 'development') {
    console.log(`[ProductFilter] Filtered ${products.length} products down to ${filtered.length} available products`);
  }
  
  return filtered;
}

// Add a new product
export async function addProduct(product) {
  try {
    console.log('[ProductApi] Starting addProduct with data:', product);
    
    // Auto-generate new inventory schema if not provided
    if (!product.inventory) {
      product.inventory = createNewProductInventory();
      console.log('[ProductApi] Auto-generated inventory schema for new product:', product.inventory);
    }
    
    // Ensure sellerId is set
    if (!product.sellerId) {
      throw new Error('Product must have a sellerId');
    }
    
    // Set creation timestamp
    product.createdAt = new Date().toISOString();
    product.updatedAt = new Date().toISOString();
    
    console.log('[ProductApi] About to call addDoc with final product data:', product);
    console.log('[ProductApi] Using collection reference:', productsCollection);
    console.log('[ProductApi] Collection path:', productsCollection.path);
    console.log('[ProductApi] Firebase app:', db.app.name);
    
    // Validate data structure before sending to Firebase
    const sanitizedProduct = { ...product };
    
    // Remove any undefined values that might cause Firebase issues
    Object.keys(sanitizedProduct).forEach(key => {
      if (sanitizedProduct[key] === undefined) {
        console.warn('[ProductApi] Removing undefined value for key:', key);
        delete sanitizedProduct[key];
      }
    });
    
    console.log('[ProductApi] Sanitized product data:', sanitizedProduct);
    
    const docRef = await addDoc(productsCollection, sanitizedProduct);
    console.log('[ProductApi] addDoc successful, document ID:', docRef.id);
    
    // Verify the document was actually created by trying to fetch it
    try {
      const verificationDoc = await getDoc(doc(db, 'products', docRef.id));
      if (verificationDoc.exists()) {
        console.log('[ProductApi] Document verification successful:', verificationDoc.data());
      } else {
        console.error('[ProductApi] Document verification failed - document does not exist!');
      }
    } catch (verifyError) {
      console.error('[ProductApi] Document verification error:', verifyError);
    }
    
    const result = { id: docRef.id, ...product };
    console.log('[ProductApi] Returning result:', result);
    
    return result;
  } catch (error) {
    console.error('[ProductApi] Error in addProduct:', error);
    console.error('[ProductApi] Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
}

// Get all products
export async function getProducts() {
  const snapshot = await getDocs(productsCollection);
  const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // Apply filtering logic for consistency
  return filterAvailableProducts(products);
}

// Get products by seller ID
export async function getProductsBySeller(sellerId, options = {}) {
  const { filterAvailable = false } = options;
  
  // Query all products for the seller (archived products are in separate collection)
  const q = query(productsCollection, where("sellerId", "==", sellerId));
  const snapshot = await getDocs(q);
  const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // If filterAvailable is true, filter to only show active products with available stock
  // This is used for customer-facing storefronts
  if (filterAvailable) {
    return filterAvailableProducts(products);
  }
  
  // Return all products for the seller (archived products are in separate collection)
  // This allows sellers to manage products regardless of stock status
  return products;
}

// Get archived products for admin use
export async function getArchivedProducts(sellerId = null) {
  try {
    const archivedProductsRef = collection(db, 'archived_products');
    let q;
    
    if (sellerId) {
      // Get archived products for a specific seller
      q = query(archivedProductsRef, where("sellerId", "==", sellerId));
    } else {
      // Get all archived products (admin view)
      q = query(archivedProductsRef);
    }
    
    const snapshot = await getDocs(q);
    const archivedProducts = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    }));
    
    console.log(`[ArchivedProducts] Found ${archivedProducts.length} archived products`);
    return archivedProducts;
  } catch (error) {
    console.error('Error fetching archived products:', error);
    throw error;
  }
}

// Get products filtered by distance from user location
export async function getProductsByDistance(userLocation, maxDistance = null) {
  try {
    // Get all products
    const productsSnapshot = await getDocs(productsCollection);
    const products = productsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        variants: data.variants || [], // Ensure variants are always included
        options: data.options || [],   // Ensure options are always included
      };
    });
    
    // Filter products by status and inventory according to PRD requirements
    const availableProducts = filterAvailableProducts(products);
    
    // If no user location, return filtered products
    if (!userLocation || !userLocation.lat || !userLocation.lng) {
      console.warn('User location not available, returning all available products');
      return availableProducts;
    }
    
    // Get all shops to get their locations
    const shopsSnapshot = await getDocs(shopsCollection);
    const shops = {};
    shopsSnapshot.docs.forEach(doc => {
      shops[doc.id] = { id: doc.id, ...doc.data() };
    });
    
    // Filter products by distance
    const filteredProducts = availableProducts.filter(product => {
      const shop = shops[product.sellerId];
      if (!shop || !shop.address || !shop.address.lat || !shop.address.lng) {
        // If shop location is not available, include the product
        // (this maintains backward compatibility)
        return true;
      }
      
      const shopLocation = {
        lat: shop.address.lat,
        lng: shop.address.lng
      };
      
      const distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        shopLocation.lat,
        shopLocation.lng
      );
      
      // Add distance to product for sorting
      product.distance = distance;
      
      // If maxDistance is specified, filter by it
      if (maxDistance !== null) {
        return distance <= maxDistance;
      }
      
      return true;
    });
    
    return filteredProducts;
  } catch (error) {
    console.error('Error fetching products by distance:', error);
    throw error;
  }
}

// Get products with distance filtering and user preferences
export async function getProductsWithDistanceFilter(userId, userLocation = null) {
  try {
    // Get user profile to get distance preference
    const { getUserProfile } = await import('./userApi');
    const userProfile = await getUserProfile(userId);
    
    // Get user's distance preference or use default
    let distancePreference = getDefaultDistancePreference();
    if (userProfile && userProfile.distancePreference) {
      distancePreference = sanitizeDistancePreference(userProfile.distancePreference);
    }
    
    // If no user location provided, try to get it
    if (!userLocation) {
      const { getUserLocation } = await import('./distanceUtils');
      userLocation = await getUserLocation();
    }
    
    // Get products filtered by distance
    const products = await getProductsByDistance(userLocation, distancePreference);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ProductFilter] getProductsWithDistanceFilter: Found ${products.length} available products for user ${userId}`);
    }
    
    // Sort by distance if location is available
    if (userLocation && userLocation.lat && userLocation.lng) {
      products.sort((a, b) => (a.distance || 0) - (b.distance || 0));
    }
    
    return {
      products,
      userLocation,
      distancePreference,
      totalFound: products.length
    };
  } catch (error) {
    console.error('Error fetching products with distance filter:', error);
    throw error;
  }
}

// Update a product
export async function updateProduct(productId, data) {
  try {
    console.log(`🔍 [updateProduct] Called with productId: ${productId}, data:`, data);
    
    if (!productId) {
      throw new Error('Product ID is required');
    }
    
    const productRef = doc(db, 'products', productId);
    console.log(`🔍 [updateProduct] Product reference:`, productRef);
    
    await updateDoc(productRef, data);
    console.log(`✅ [updateProduct] Successfully updated product ${productId}`);
  } catch (error) {
    console.error(`❌ [updateProduct] Error updating product ${productId}:`, error);
    throw error;
  }
}

// Archive a product (move to archived_products collection)
export async function archiveProduct(productId) {
  try {
    console.log(`🔍 [archiveProduct] Starting archival process for product: ${productId}`);
    
    if (!productId) {
      throw new Error('Product ID is required');
    }
    
    // Get current user info for debugging
    const auth = getAuth();
    const currentUser = auth.currentUser;
    console.log(`🔍 [archiveProduct] Current user:`, {
      uid: currentUser?.uid,
      email: currentUser?.email,
      emailVerified: currentUser?.emailVerified
    });
    
    // Get user role from Firestore for debugging
    if (currentUser) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log(`🔍 [archiveProduct] User role:`, userData.role);
          console.log(`🔍 [archiveProduct] User data:`, userData);
        } else {
          console.log(`🔍 [archiveProduct] User document does not exist`);
        }
      } catch (roleError) {
        console.error(`🔍 [archiveProduct] Error getting user role:`, roleError);
      }
    }
    
    const productRef = doc(db, 'products', productId);
    const archivedProductsRef = collection(db, 'archived_products');
    
    console.log(`🔍 [archiveProduct] References created - Product: ${productRef.path}, Archive: ${archivedProductsRef.path}`);
    
    // Get the product data first
    console.log(`🔍 [archiveProduct] Fetching product data...`);
    const productDoc = await getDoc(productRef);
    if (!productDoc.exists()) {
      console.error(`❌ [archiveProduct] Product ${productId} does not exist in products collection`);
      console.error(`❌ [archiveProduct] Product path: ${productRef.path}`);
      
      // Check if the product might already be in archived_products
      const archivedQuery = query(archivedProductsRef, where('original_product_id', '==', productId));
      const archivedSnapshot = await getDocs(archivedQuery);
      
      if (archivedSnapshot.docs.length > 0) {
        console.log(`🔍 [archiveProduct] Product ${productId} already exists in archived_products collection`);
        return {
          originalId: productId,
          archivedId: archivedSnapshot.docs[0].id,
          archivedAt: archivedSnapshot.docs[0].data().archived_at,
          alreadyArchived: true
        };
      }
      
      throw new Error(`Product ${productId} not found in products collection. It may have already been deleted or archived.`);
    }
    
    const productData = productDoc.data();
    console.log(`🔍 [archiveProduct] Product data retrieved:`, productData);
    console.log(`🔍 [archiveProduct] Product sellerId:`, productData.sellerId);
    console.log(`🔍 [archiveProduct] Current user UID:`, currentUser?.uid);
    console.log(`🔍 [archiveProduct] Seller match:`, productData.sellerId === currentUser?.uid);
    
    // Prepare archived product data
    const archivedProduct = {
      ...productData,
      original_product_id: productId, // Keep reference to original ID
      archived_at: new Date().toISOString(),
      archived_by: 'seller', // Could be enhanced to track who archived it
      archived_reason: 'seller_deletion' // Could be enhanced to track why it was archived
    };
    
    console.log(`🔍 [archiveProduct] Prepared archived product data:`, archivedProduct);
    
    // Add to archived_products collection
    console.log(`🔍 [archiveProduct] Adding to archived_products collection...`);
    let archivedDocRef;
    
    try {
      archivedDocRef = await addDoc(archivedProductsRef, archivedProduct);
      console.log(`🔍 [archiveProduct] Successfully added to archive with ID: ${archivedDocRef.id}`);
    } catch (addError) {
      console.error(`❌ [archiveProduct] Failed to add to archive collection:`, addError);
      
      // If the collection doesn't exist, try creating it with a test document first
      if (addError.code === 'permission-denied' || addError.code === 'not-found') {
        console.log(`🔍 [archiveProduct] Attempting to create archived_products collection...`);
        try {
          // Try to create the collection by adding a test document
          const testDoc = await addDoc(archivedProductsRef, {
            temp: true,
            created_at: new Date().toISOString(),
            note: 'Temporary document to initialize archived_products collection'
          });
          console.log(`🔍 [archiveProduct] Collection created with test doc: ${testDoc.id}`);
          
          // Now try to add the actual archived product
          archivedDocRef = await addDoc(archivedProductsRef, archivedProduct);
          console.log(`🔍 [archiveProduct] Successfully added archived product: ${archivedDocRef.id}`);
          
          // Clean up test document
          await deleteDoc(doc(db, 'archived_products', testDoc.id));
          console.log(`🔍 [archiveProduct] Test document cleaned up`);
        } catch (createError) {
          console.error(`❌ [archiveProduct] Failed to create collection:`, createError);
          throw new Error(`Failed to create archived_products collection: ${createError.message}`);
        }
      } else {
        throw addError;
      }
    }
    
    // Delete from original products collection
    console.log(`🔍 [archiveProduct] Deleting from original products collection...`);
    await deleteDoc(productRef);
    console.log(`🔍 [archiveProduct] Successfully deleted from original collection`);
    
    console.log(`✅ [archiveProduct] Successfully moved product ${productId} to archived_products collection as ${archivedDocRef.id}`);
    
    return {
      originalId: productId,
      archivedId: archivedDocRef.id,
      archivedAt: archivedProduct.archived_at
    };
  } catch (error) {
    console.error(`❌ [archiveProduct] Error archiving product ${productId}:`, error);
    console.error(`❌ [archiveProduct] Error details:`, {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
}

// Delete a product (permanent delete - for admin use only)
export async function deleteProduct(productId) {
  const productRef = doc(db, 'products', productId);
  await deleteDoc(productRef);
}

// Helper function to get default distance preference
function getDefaultDistancePreference() {
  return 25; // Default to 25 miles
} 

// Helper function to calculate inventory alerts for seller hub
export function calculateInventoryAlerts(products) {
  if (!Array.isArray(products)) {
    return { lowStock: 0, outOfStock: 0, total: 0 };
  }

  let lowStockCount = 0;
  let outOfStockCount = 0;

  products.forEach(product => {
    // Skip invalid products
    if (!product || typeof product !== 'object') {
      return;
    }

    let hasStock = false;
    let isLowStock = false;

    // Check if product has unlimited stock
    if (product.unlimited === true || product.stock === null) {
      hasStock = true;
    } else {
      // Check total available stock
      let totalStock = 0;
      let hasLowStockDay = false;

      // If product has daily stock tracking, check each day
      if (product.dailyStock && typeof product.dailyStock === 'object') {
        Object.values(product.dailyStock).forEach(dayStock => {
          const numStock = Number(dayStock) || 0;
          totalStock += numStock;
          // Check if any single day has only 1 stock left
          if (numStock === 1) {
            hasLowStockDay = true;
          }
        });
      } else if (product.stock !== undefined) {
        // Use regular stock field
        totalStock = Number(product.stock) || 0;
        if (totalStock === 1) {
          hasLowStockDay = true;
        }
      }

      // Check variants for low stock
      if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
        product.variants.forEach(variant => {
          if (variant && typeof variant === 'object' && variant.stock !== undefined) {
            const variantStock = Number(variant.stock) || 0;
            totalStock = Math.max(totalStock, variantStock);
            if (variantStock === 1) {
              hasLowStockDay = true;
            }
          }
        });
      }

      hasStock = totalStock > 0;
      isLowStock = hasLowStockDay || totalStock === 1;
    }

    if (!hasStock) {
      outOfStockCount++;
    } else if (isLowStock) {
      lowStockCount++;
    }
  });

  return {
    lowStock: lowStockCount,
    outOfStock: outOfStockCount,
    total: lowStockCount + outOfStockCount
  };
}

// Helper function to filter products by inventory status
export function filterProductsByInventoryStatus(products, status) {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.filter(product => {
    if (!product || typeof product !== 'object') {
      return false;
    }

    let hasStock = false;
    let isLowStock = false;

    // Check if product has unlimited stock
    if (product.unlimited === true || product.stock === null) {
      hasStock = true;
    } else {
      // Check total available stock
      let totalStock = 0;
      let hasLowStockDay = false;

      // If product has daily stock tracking, check each day
      if (product.dailyStock && typeof product.dailyStock === 'object') {
        Object.values(product.dailyStock).forEach(dayStock => {
          const numStock = Number(dayStock) || 0;
          totalStock += numStock;
          // Check if any single day has only 1 stock left
          if (numStock === 1) {
            hasLowStockDay = true;
          }
        });
      } else if (product.stock !== undefined) {
        // Use regular stock field
        totalStock = Number(product.stock) || 0;
        if (totalStock === 1) {
          hasLowStockDay = true;
        }
      }

      // Check variants for low stock
      if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
        product.variants.forEach(variant => {
          if (variant && typeof variant === 'object' && variant.stock !== undefined) {
            const variantStock = Number(variant.stock) || 0;
            totalStock = Math.max(totalStock, variantStock);
            if (variantStock === 1) {
              hasLowStockDay = true;
            }
          }
        });
      }

      hasStock = totalStock > 0;
      isLowStock = hasLowStockDay || totalStock === 1;
    }

    switch (status) {
      case 'inStock':
        return hasStock && !isLowStock;
      case 'lowStock':
        return hasStock && isLowStock;
      case 'outOfStock':
        return !hasStock;
      case 'needsAttention': // Low stock + out of stock
        return !hasStock || (hasStock && isLowStock);
      default:
        return true; // All products
    }
  });
}

// 🚀 NEW: High-Performance Food Discovery Queries using searchableFields
// These functions use the new optimized database structure for 10-100x faster queries

/**
 * Get products optimized for food discovery using searchableFields
 * This replaces the old complex queries with fast, indexed lookups
 */
export async function getProductsForFoodDiscovery(filters = {}) {
  try {
    const startTime = Date.now();
    console.log('🚀 Using optimized food discovery query with searchableFields');
    
    // Build query constraints based on filters
    const constraints = [
      where('published', '==', true),
      where('searchableFields.hasInventory', '==', true)
    ];
    
    // Add category filters
    if (filters.listingType) {
      constraints.push(where('searchableFields.listingType', '==', filters.listingType));
    }
    
    if (filters.itemCategory) {
      constraints.push(where('searchableFields.itemCategory', '==', filters.itemCategory));
    }
    
    if (filters.cuisine) {
      constraints.push(where('searchableFields.cuisine', '==', filters.cuisine));
    }
    
    // Add dietary filters
    if (filters.dietaryTags && filters.dietaryTags.length > 0) {
      constraints.push(where('searchableFields.dietaryTags', 'array-contains-any', filters.dietaryTags));
    }
    
    // Add availability filters
    if (filters.availableNow) {
      constraints.push(where('searchableFields.isAvailableNow', '==', true));
    }
    
    if (filters.hasDelivery) {
      constraints.push(where('searchableFields.hasDelivery', '==', true));
    }
    
    if (filters.hasPickup) {
      constraints.push(where('searchableFields.hasPickup', '==', true));
    }
    
    // Add price range filters
    if (filters.priceRange) {
      constraints.push(where('searchableFields.priceRange', '==', filters.priceRange));
    }
    
    // Add availability day filters
    if (filters.availableDays && filters.availableDays.length > 0) {
      constraints.push(where('searchableFields.availableDays', 'array-contains-any', filters.availableDays));
    }
    
    // Execute optimized query
    const q = query(productsCollection, ...constraints);
    const snapshot = await getDocs(q);
    
    const products = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter out products from inactive stores
    const activeShopsSnapshot = await getDocs(query(
      shopsCollection,
      where('status', '==', 'active')
    ));
    
    const activeSellerIds = new Set(
      activeShopsSnapshot.docs.map(doc => doc.data().sellerId).filter(Boolean)
    );
    
    const filteredProducts = products.filter(product => 
      !product.sellerId || activeSellerIds.has(product.sellerId)
    );
    
    console.log(`🚀 Optimized query returned ${filteredProducts.length} products (${products.length - filteredProducts.length} filtered from inactive stores) in ${Date.now() - startTime}ms`);
    return filteredProducts;
    
  } catch (error) {
    console.error('Error in optimized food discovery query:', error);
    throw error;
  }
}

/**
 * Get products available now (real-time availability)
 * Uses the pre-computed isAvailableNow field for instant results
 */
export async function getProductsAvailableNow(filters = {}) {
  try {
    console.log('🚀 Querying products available now using searchableFields');
    
    const constraints = [
      where('published', '==', true),
      where('searchableFields.hasInventory', '==', true),
      where('searchableFields.isAvailableNow', '==', true)
    ];
    
    // Add additional filters
    if (filters.listingType) {
      constraints.push(where('searchableFields.listingType', '==', filters.listingType));
    }
    
    if (filters.itemCategory) {
      constraints.push(where('searchableFields.itemCategory', '==', filters.itemCategory));
    }
    
    const q = query(productsCollection, ...constraints);
    const snapshot = await getDocs(q);
    
    const products = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`🚀 Available now query returned ${products.length} products`);
    return products;
    
  } catch (error) {
    console.error('Error querying available now products:', error);
    throw error;
  }
}

/**
 * Get products by dietary restrictions (high-performance)
 * Uses the pre-computed dietary fields for instant filtering
 */
export async function getProductsByDietaryRestrictions(dietaryTags, additionalFilters = {}) {
  try {
    console.log('🚀 Querying products by dietary restrictions using searchableFields');
    
    const constraints = [
      where('published', '==', true),
      where('searchableFields.hasInventory', '==', true),
      where('searchableFields.hasDietaryRestrictions', '==', true),
      where('searchableFields.dietaryTags', 'array-contains-any', dietaryTags)
    ];
    
    // Add additional filters
    if (additionalFilters.listingType) {
      constraints.push(where('searchableFields.listingType', '==', additionalFilters.listingType));
    }
    
    if (additionalFilters.cuisine) {
      constraints.push(where('searchableFields.cuisine', '==', additionalFilters.cuisine));
    }
    
    const q = query(productsCollection, ...constraints);
    const snapshot = await getDocs(q);
    
    const products = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`🚀 Dietary query returned ${products.length} products`);
    return products;
    
  } catch (error) {
    console.error('Error querying products by dietary restrictions:', error);
    throw error;
  }
}

/**
 * Get products by price range (high-performance)
 * Uses the pre-computed priceRange field for instant filtering
 */
export async function getProductsByPriceRange(priceRange, additionalFilters = {}) {
  try {
    console.log('🚀 Querying products by price range using searchableFields');
    
    const constraints = [
      where('published', '==', true),
      where('searchableFields.hasInventory', '==', true),
      where('searchableFields.priceRange', '==', priceRange)
    ];
    
    // Add additional filters
    if (additionalFilters.listingType) {
      constraints.push(where('searchableFields.listingType', '==', additionalFilters.listingType));
    }
    
    if (additionalFilters.itemCategory) {
      constraints.push(where('searchableFields.itemCategory', '==', additionalFilters.itemCategory));
    }
    
    const q = query(productsCollection, ...constraints);
    const snapshot = await getDocs(q);
    
    const products = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`🚀 Price range query returned ${products.length} products`);
    return products;
    
  } catch (error) {
    console.error('Error querying products by price range:', error);
    throw error;
  }
} 